import { FC, useState, useEffect } from "react"
import { Loader2, Clipboard, Check } from "lucide-react"
import { PricingLog, RateflowCard } from "../../../../types"
import SidePanel from "../../side-panel"
import Tabs from "../../tabs"
import ReactJSONEditor, { JSONEditorSelection } from "../../react-json-editor"
import If from "../../if"
import CardQuoteDetails from "../../card-quote-details"
import Cards from "./cards"
import { DiagnosisResults } from "./diagnosis-results"
import {
  useFindRateflowLogByIdQuery,
  useFetchAiPricingDiagnosisQuery
} from "../../../../redux/queries"
import {
  DEFAULT_DATETIME_SECONDS_FORMAT,
  formatDateTimeFromTimestamp
} from "../../../utils"

interface Props {
  open: boolean
  onClose: () => void
  data?: PricingLog
}

const TABS = [
  {
    id: "cards",
    label: "Cards"
  },
  {
    id: "details",
    label: "Details"
  }
]

type TabId = "cards" | "details"

type JsonViewMode = "tree" | "pretty"

const getValueAtPath = (obj: any, path: (string | number)[]): any => {
  return path.reduce((current, key) => current?.[key], obj)
}

const PricingLogCardDetails: FC<Props> = ({ open, onClose, data }) => {
  const [currentTab, setCurrentTab] = useState<TabId>("cards")
  const [jsonViewMode, setJsonViewMode] = useState<JsonViewMode>("tree")
  const [copied, setCopied] = useState(false)
  const [selectedNode, setSelectedNode] = useState<JSONEditorSelection>(null)
  const [openCardQuoteDetails, setOpenCardQuoteDetails] = useState<{
    isOpen: boolean
    data?: RateflowCard
  }>({
    isOpen: false
  })
  const [shouldFetchDiagnosis, setShouldFetchDiagnosis] = useState(false)

  const { data: rate, isLoading } = useFindRateflowLogByIdQuery(
    data?.id as string,
    {
      skip: !data?.id
    }
  )

  // Extract quote_id from the rate data
  // When there are no cards (pricing failed), use the log ID itself
  const quoteId =
    rate?.cards?.[0]?.quote_id?.toString() ||
    rate?.full_response?.products?.[0]?.quote_id?.toString() ||
    data?.id

  // Fetch diagnosis only when button is clicked and quote_id is available
  const {
    data: diagnosis,
    isLoading: isDiagnosisLoading,
    error: diagnosisError
  } = useFetchAiPricingDiagnosisQuery(quoteId as string, {
    skip: !shouldFetchDiagnosis || !quoteId
  })

  const onTabChange = (tabId: string) => setCurrentTab(tabId as TabId)
  const onViewDetails = (data: RateflowCard) => {
    setOpenCardQuoteDetails({
      isOpen: true,
      data
    })
  }
  const onCloseViewDetails = () => {
    setOpenCardQuoteDetails({
      isOpen: false
    })
  }

  const onDiagnosePricing = () => {
    setShouldFetchDiagnosis(true)
  }

  const onCopyJson = async () => {
    const dataToCopy = selectedNode?.path?.length
      ? getValueAtPath(rate, selectedNode.path)
      : rate
    const jsonString = JSON.stringify(dataToCopy, null, 2)
    await navigator.clipboard.writeText(jsonString)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const onSelectionChange = (selection: JSONEditorSelection) => {
    setSelectedNode(selection)
  }

  const clearSelection = () => {
    setSelectedNode(null)
  }

  const handleClose = () => {
    setShouldFetchDiagnosis(false)
    onClose()
  }

  // Reset diagnosis state when panel opens/closes or data changes
  useEffect(() => {
    if (!open) {
      setShouldFetchDiagnosis(false)
    }
  }, [open, data?.id])

  const loanAmount = rate?.full_request?.bb_request?.loan_amount || 0

  return (
    <>
      <SidePanel
        title={data?.ob_profile_name}
        subTitle={`Created on ${formatDateTimeFromTimestamp(
          data?.timestamp as string,
          DEFAULT_DATETIME_SECONDS_FORMAT
        )}`}
        open={open}
        onClose={handleClose}
      >
        <div className="-mx-6 h-full">
          <If
            condition={!isLoading}
            then={
              <>
                <Tabs
                  currentTab={currentTab}
                  tabs={TABS}
                  onChange={onTabChange}
                />
                <If
                  condition={currentTab === "cards"}
                  then={
                    <>
                      <If
                        condition={!!rate?.cards?.length}
                        then={
                          <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6">
                            {rate?.cards?.map(rate => (
                              <Cards
                                key={rate.hash}
                                data={rate}
                                loanAmount={loanAmount}
                                onViewDetails={onViewDetails}
                              />
                            ))}
                          </div>
                        }
                        else={
                          <div className="p-6">
                            <If
                              condition={!shouldFetchDiagnosis}
                              then={
                                <div className="flex flex-col items-center justify-center py-12">
                                  <p className="text-gray-600 mb-4">
                                    No cards found for this log.
                                  </p>
                                  <button
                                    type="button"
                                    className="px-6 py-2 bg-primary-alt-400 text-white rounded-md hover:bg-primary-alt-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    onClick={onDiagnosePricing}
                                    disabled={!quoteId}
                                  >
                                    Diagnose Pricing
                                  </button>
                                  {!quoteId && (
                                    <p className="text-sm text-gray-500 mt-2">
                                      No quote ID available for diagnosis
                                    </p>
                                  )}
                                </div>
                              }
                            />
                            <If
                              condition={
                                shouldFetchDiagnosis && isDiagnosisLoading
                              }
                              then={
                                <div className="flex flex-col items-center justify-center py-12">
                                  <Loader2
                                    className="animate-spin h-8 w-8 text-primary-alt-400"
                                    aria-hidden="true"
                                  />
                                  <p className="text-gray-600 mt-4">
                                    Analyzing pricing...
                                  </p>
                                </div>
                              }
                            />
                            <If
                              condition={
                                shouldFetchDiagnosis &&
                                !!diagnosis &&
                                !isDiagnosisLoading
                              }
                              then={<DiagnosisResults diagnosis={diagnosis!} />}
                            />
                            <If
                              condition={
                                shouldFetchDiagnosis && !!diagnosisError
                              }
                              then={
                                <div className="flex flex-col items-center justify-center py-12">
                                  <p className="text-red-600 mb-4">
                                    Failed to fetch diagnosis. Please try again.
                                  </p>
                                  <button
                                    type="button"
                                    className="px-6 py-2 bg-primary-alt-400 text-white rounded-md hover:bg-primary-alt-500"
                                    onClick={onDiagnosePricing}
                                  >
                                    Retry
                                  </button>
                                </div>
                              }
                            />
                          </div>
                        }
                      />
                    </>
                  }
                />
                <If
                  condition={currentTab === "details"}
                  then={
                    <div className="pt-6">
                      <div className="px-6 pb-4 flex gap-2">
                        <button
                          type="button"
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                            jsonViewMode === "tree"
                              ? "bg-primary-alt-400 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                          onClick={() => setJsonViewMode("tree")}
                        >
                          Tree View
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                            jsonViewMode === "pretty"
                              ? "bg-primary-alt-400 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                          onClick={() => setJsonViewMode("pretty")}
                        >
                          Pretty Print
                        </button>
                      </div>
                      <If
                        condition={jsonViewMode === "tree"}
                        then={
                          <ReactJSONEditor
                            name="plDetails"
                            json={rate}
                            mode="view"
                            onSelectionChange={onSelectionChange}
                          />
                        }
                        else={
                          <div className="mx-6 mb-6">
                            {selectedNode?.path?.length ? (
                              <div className="mb-2 flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-mono">
                                  {selectedNode.path.join(".")}
                                </span>
                                <button
                                  type="button"
                                  onClick={clearSelection}
                                  className="text-xs text-primary-alt-400 hover:underline"
                                >
                                  Show all
                                </button>
                              </div>
                            ) : null}
                            <div className="relative group">
                              <button
                                type="button"
                                onClick={onCopyJson}
                                className="absolute top-2 right-5 p-2 rounded-md bg-white border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100"
                                title="Copy to clipboard"
                              >
                                {copied ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Clipboard className="h-4 w-4 text-gray-600" />
                                )}
                              </button>
                              <pre className="p-4 text-sm overflow-auto whitespace-pre-wrap font-mono bg-gray-50 rounded-md border border-gray-200 max-h-[600px]">
                                {JSON.stringify(
                                  selectedNode?.path?.length
                                    ? getValueAtPath(rate, selectedNode.path)
                                    : rate,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          </div>
                        }
                      />
                    </div>
                  }
                />
              </>
            }
            else={
              <div className="h-full flex justify-center items-center">
                <Loader2
                  className="animate-spin h-16 w-16 text-primary-alt-400"
                  aria-hidden="true"
                />
              </div>
            }
          />
        </div>
      </SidePanel>
      <If
        condition={openCardQuoteDetails.isOpen}
        then={
          <SidePanel
            title="Quote details"
            open={openCardQuoteDetails.isOpen}
            onClose={onCloseViewDetails}
            isBack
          >
            <div className="h-full flex flex-col">
              <CardQuoteDetails
                row={openCardQuoteDetails.data}
                loanAmount={loanAmount}
              />
            </div>
          </SidePanel>
        }
      />
    </>
  )
}

export default PricingLogCardDetails
